import dayjs from 'dayjs';

import { DATE_MDY_FULL } from 'constants/dateTime';

const DEFAULT_TRUNCATE_LIMIT = 45;

/* eslint-disable */
export const formatURL = (url, queryParams = {}) => {
  const queryParamString = Object.keys(queryParams)
    .filter(param => queryParams[param] !== null && queryParams[param] !== undefined)
    .map(param => `${param}=${queryParams[param]}`)
    .join('&');

  return queryParamString.length > 0 ? `${url}?${queryParamString}` : url;
};

export const toTitleCase = word => {
  if (word.toUpperCase() === 'CHOWNOW') return 'ChowNow';

  return `${word.charAt(0).toUpperCase()}${word.substr(1).toLowerCase()}`;
};

/**
 * Given an array of items, normalize them by a property ('key')
 * @example
 *  normalizeByKey([{ id: 'x', name: 'foo' }], 'id') => {x: {name: 'foo'}}
 *
 * @param items: Array of items to transform
 * @param key: The key to normalize each item by
 * @param transformItem: (Optional) Callback to perform data transform on item
 * @returns Object with keys of each property 'key' in item
 */
export const normalizeByKey = (items, key, transformItem = item => ({ ...item })) => {
  return items.reduce((previous, current) => {
    previous[current[key]] = transformItem(current);
    return previous;
  }, {});
};

export const capitalizeWords = string => {
  return string.replace(/(?:^|\s)\S/g, a => {
    return a.toUpperCase();
  });
};

export const alphabeticalCompareForSorting = (string, string2) => {
  if (string.label?.toLowerCase() < string2.label?.toLowerCase()) {
    return -1;
  }
  if (string.label.toLowerCase() < string2.label.toLowerCase()) {
    return -1;
  }
  return 0;
};

export function formatCurrency(value) {
  const formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  });

  return formatter.format(value);
}

export const valueIsPresent = value => {
  // Undefiend is needed because of historical data from cuboh-core
  return value && !['Undefined', 'Undefiend', '', 'None'].includes(value);
};

export async function requestChunker(axios, endpoint, parameterArray) {
  const maxChunkSize = 200;
  const allowedConcurrentRequests = 5;
  const parameterChunks = [];
  // split the parameter array into manageable arrays
  for (let i = 0; i < parameterArray.length; i += maxChunkSize) {
    const chunk = parameterArray.slice(i, i + maxChunkSize);
    parameterChunks.push(chunk);
  }
  // for each array of parameters, make the request
  let allData = [];
  while (parameterChunks.length) {
    const currentBatchReturn = await Promise.all(
      parameterChunks
        .splice(0, allowedConcurrentRequests) // perform the queries in batches to avoid throttling failures
        .map(chunk => axios.get(`${endpoint}${chunk}`)),
    );
    allData = [...allData, ...currentBatchReturn];
  }

  // flatten the data under one object and return in a familiar structure
  return {
    data: allData.reduce((accumulator, { data }) => {
      // check the data return type
      if (Array.isArray(data) && !Array.isArray(accumulator)) {
        return [...data];
      } else if (Array.isArray(data) && Array.isArray(accumulator)) {
        return [...accumulator, ...data];
      } else {
        // loop over each object key to prevent overwriting data
        Object.keys(data).forEach(key => {
          if (!accumulator[key]) {
            accumulator[key] = data[key];
          } else if (Array.isArray(accumulator[key])) {
            accumulator[key] = [...accumulator[key], ...data[key]];
          } else {
            accumulator[key] = { ...accumulator[key], ...data[key] };
          }
        }, {});
      }

      return accumulator;
    }, {}),
  };
}

export const chunkData = (dataToChunk, chunkSize) => {
  if (!dataToChunk.length) {
    return [];
  }
  const head = dataToChunk.slice(0, chunkSize);
  const tail = dataToChunk.slice(chunkSize);
  return [head, ...chunkData(tail, chunkSize)];
};

export const formatMultipleTagType = (response, type, count = null) => {
  switch (type) {
    case 'date':
      return count
        ? `${dayjs(response).format(DATE_MDY_FULL)} (${count})`
        : dayjs(response).format(DATE_MDY_FULL);
    case 'currency':
      return count ? `${formatCurrency(response)} (${count})` : formatCurrency(response);
    default:
      return count ? `${response} (${count})` : response;
  }
};

/** Helper method to make array unique */
export const normalizeArray = array =>
  Array.from(new Set(array)).filter(value => value !== undefined);

export const truncateString = (string, limit = DEFAULT_TRUNCATE_LIMIT) => {
  if (string.length > limit) {
    return string.substring(0, limit) + '...';
  } else {
    return string;
  }
};

export const formatScheduleTime = time => {
  const [hour, minute] = time.split(':');
  const hourInt = parseInt(hour) % 24;

  // 0:30 -> 12:30 AM
  if (hourInt === 0) {
    return `12:${minute} AM`;
  }

  // 07:30 -> 7:30 AM
  if (hourInt < 12) {
    return `${hourInt}:${minute} AM`;
  }

  // 12:30 -> 12:30 PM
  if (hourInt === 12) {
    return `${hourInt}:${minute} PM`;
  }

  // 14:30 -> 2:30:pm
  return `${hourInt - 12}:${minute} PM`;
};

export const formatPercent = percent => `${parseFloat(percent).toFixed(2)}%`;
